import { ColumnDef } from "@tanstack/react-table";
import { EmptyTableMessage } from "@/core/components/empty-table-message";
import { SimpleTable } from "@/core/components/simple-table";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type { ReviewWithSearchableFields } from "@/reviews/review";
import { AttributionCol } from "./cols/attribution-col";
import { DateCol } from "./cols/date-col";
import { PlatformCol } from "./cols/platform-col";
import { RatingCol } from "./cols/rating-col";
import { ReviewBodyCol } from "./cols/review-body-col";
import { ReviewerNameCol } from "./cols/reviewer-name-col";

interface ReviewsTableProps {
  data: ReviewWithSearchableFields[];
  isLoading: boolean;
  selectedRowId?: string;
  onRowClick?: (row: ReviewWithSearchableFields) => void;
}

const ReviewsEmptyState = () => (
  <Stack gap="sm" align="center">
    <Typography variant="subtitleW1">No five-star reviews yet</Typography>
    <Typography variant="bodyW1" textColor="secondary">
      Five-star reviews will appear here once customers leave feedback.
    </Typography>
  </Stack>
);

export const ReviewsTable = ({
  data,
  isLoading,
  selectedRowId,
  onRowClick,
}: ReviewsTableProps) => {
  const columns: ColumnDef<ReviewWithSearchableFields>[] = [
    ReviewerNameCol<ReviewWithSearchableFields>(),
    DateCol<ReviewWithSearchableFields>(),
    PlatformCol<ReviewWithSearchableFields>(),
    RatingCol<ReviewWithSearchableFields>(),
    ReviewBodyCol<ReviewWithSearchableFields>(),
    AttributionCol<ReviewWithSearchableFields>(),
  ];

  if (data.length === 0 && !isLoading) {
    return (
      <div className="mr-5">
        <EmptyTableMessage>
          <ReviewsEmptyState />
        </EmptyTableMessage>
      </div>
    );
  }

  return (
    <SimpleTable
      data={data}
      columns={columns}
      isLoading={isLoading}
      onRowClick={onRowClick}
      selectedRowId={selectedRowId}
    />
  );
};
